import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Rating,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack, LocationOn, Language, Favorite, FavoriteBorder } from '@mui/icons-material';
import { Event, Location } from '../types';
import { eventAPI, locationAPI } from '../services/api';
import { parseEventSlug } from '../utils/urlUtils';
import { updateSEOTags } from '../utils/seoUtils';
import { useFavorites } from '../contexts/FavoritesContext';
import MapView from '../components/MapView';

const EventPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) {
        setError('Invalid event URL');
        setLoading(false);
        return;
      }

      const uniqueId = parseEventSlug(slug);
      if (!uniqueId) {
        setError('Invalid event URL');
        setLoading(false);
        return;
      }

      try {
        // Fetch event details
        const eventData = await eventAPI.getByUniqueId(uniqueId);
        setEvent(eventData);

        // Fetch location details
        const locationData = await locationAPI.getByUniqueId(eventData.locationId);
        setLocation(locationData);

        // Update SEO tags
        const title = `${eventData.eventTitle} - FindFunNow`;
        const description = eventData.subhead || `Experience ${eventData.eventTitle}. ${eventData.price ? `Starting at ${eventData.price}` : 'Book now'} with FindFunNow.`;
        const keywords = eventData.seoKeywords || [
          eventData.category || 'activity',
          locationData.name,
          'travel',
          'booking',
          'experience'
        ];

        updateSEOTags({
          title,
          description,
          keywords,
          locationName: locationData.name
        });

      } catch (error: any) {
        console.error('Error fetching event:', error);
        setError('Event not found');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [slug]);

  const handleBookNow = async () => {
    if (!event) return;
    
    try {
      await eventAPI.incrementClick(event.uniqueId);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFavoriteClick = () => {
    if (!event) return;
    
    if (isFavorite(event.uniqueId)) {
      removeFromFavorites(event.uniqueId);
    } else {
      addToFavorites(event);
    }
  };

  const isEventFavorite = event ? isFavorite(event.uniqueId) : false;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !event || !location) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Event not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button 
        startIcon={<ArrowBack />} 
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Grid container spacing={4}>
        {/* Event Image */}
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={event.imageUrl}
            alt={event.eventTitle}
            sx={{
              width: '100%',
              height: { xs: 300, md: 400 },
              objectFit: 'cover',
              borderRadius: 2,
            }}
          />
        </Grid>

        {/* Event Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            {event.eventTitle}
          </Typography>

          {event.price && (
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#059669',
                fontWeight: 700,
                mb: 2,
              }}
            >
              {event.price}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating value={event.starRating} readOnly size="large" />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              ({event.starRating})
            </Typography>
          </Box>

          {event.category && (
            <Chip
              label={
                event.category === 'hotel' ? 'Hotel' : 
                event.category === 'vacation_rentals' ? 'Vacation Rental' :
                event.category === 'activities' ? 'Activities' : 
                event.category === 'event' ? 'Event' :
                event.category
              }
              sx={{
                bgcolor: event.category === 'hotel' || event.category === 'vacation_rentals' ? '#6366f1' : 
                         event.category === 'activities' ? '#0ea5e9' : '#f59e0b',
                color: 'white',
                fontWeight: 600,
                mb: 3,
              }}
            />
          )}

          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.6,
              mb: 3,
            }}
          >
            {event.subhead}
          </Typography>

          {event.address?.street && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
              <LocationOn sx={{ color: 'text.secondary', mt: 0.5, fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="body1">
                  {event.address.street}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {event.address.city}, {event.address.state} {event.address.zip}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={isEventFavorite ? 'contained' : 'outlined'}
                onClick={handleFavoriteClick}
                startIcon={isEventFavorite ? <Favorite /> : <FavoriteBorder />}
                sx={{
                  background: isEventFavorite ? '#ef4444' : 'transparent',
                  color: isEventFavorite ? 'white' : '#ef4444',
                  borderColor: '#ef4444',
                  py: 1.5,
                  '&:hover': {
                    background: isEventFavorite ? '#dc2626' : 'rgba(239, 68, 68, 0.1)',
                    borderColor: '#ef4444',
                  }
                }}
              >
                {isEventFavorite ? 'Favorited' : 'Add to Favorites'}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBookNow}
                startIcon={<Language />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  fontWeight: 600,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  }
                }}
              >
                Book Now
              </Button>
            </Grid>
          </Grid>

          {/* SEO Keywords */}
          {event.seoKeywords && event.seoKeywords.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {event.seoKeywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: '#6366f1', color: '#6366f1' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Grid>

        {/* Map */}
        {event.address?.latitude && event.address?.longitude && (
          <Grid item xs={12}>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  Location
                </Typography>
                <Box sx={{ height: 300, borderRadius: 2, overflow: 'hidden' }}>
                  <MapView 
                    events={[event]}
                    selectedEvent={event}
                    locationName={`${event.address.city}, ${event.address.state}`}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default EventPage;